import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { BillingInformation } from '@app/api';

import { LargeModaleWithLeftPane } from '@app/core';
import { useTranslation } from 'react-i18next';
import { BillingInformationVerificationModalPane } from './BillingInformationVerificationModalPane';
import { OrganizationSelectionStep } from './OrganizationSelectionStep';
import { Stepper } from 'components/Stepper';
import { useState } from 'react';
import { AddressSelectionStep } from './AddressSelectionStep';
import { FinalBillingInformationVerificationStep } from './FinalBillingInformationVerificationStep';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 4,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mainContent: {
    flex: 1,
  },
  paneFlex: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase * 4,
  },
  steps: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

interface Props {
  billingInformation: BillingInformation;
  isOpen: boolean;
  onRequestClose: () => any;
}

enum Steps {
  BILLING_ORGANIZATION = 0,
  BILLING_ADDRESS = 1,
  BILLING_CONFIRMATION = 2,
}

export const BillingInformationVerificationModal = ({
  isOpen,
  onRequestClose,
  billingInformation,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [step, setStep] = useState<Steps>(
    billingInformation.provider === 'STRIPE'
      ? Steps.BILLING_CONFIRMATION
      : Steps.BILLING_ORGANIZATION,
  );

  if (!billingInformation) {
    return;
  }

  return (
    <LargeModaleWithLeftPane
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      paneTitle={t('admin.billing.billingInfo.modalTitle')}
      paneClassName={classes.paneFlex}
      paneContent={
        <BillingInformationVerificationModalPane
          billingInformation={billingInformation}
        />
      }
    >
      <div className={classes.container}>
        <div className={classes.mainContent}>
          {step === Steps.BILLING_ORGANIZATION && (
            <OrganizationSelectionStep
              billingInformation={billingInformation}
              onConfirm={() => setStep(Steps.BILLING_ADDRESS)}
            />
          )}
          {step === Steps.BILLING_ADDRESS && (
            <AddressSelectionStep
              billingInformation={billingInformation}
              onConfirm={() => setStep(Steps.BILLING_CONFIRMATION)}
            />
          )}
          {step === Steps.BILLING_CONFIRMATION && (
            <FinalBillingInformationVerificationStep
              billingInformation={billingInformation}
              onConfirm={() => onRequestClose()}
            />
          )}
        </div>
        <div className={classes.steps}>
          <Stepper
            activeStep={step}
            requestStepChange={setStep}
            stepsCount={Steps.BILLING_CONFIRMATION + 1}
          />
        </div>
      </div>
    </LargeModaleWithLeftPane>
  );
};
